<template>
  <div
    class="modern-container"
    role="main"
    aria-label="Demandes de modification de profil"
  >
    <div class="tickets-header">
      <div class="header-content">
        <h1 class="simple-title">
          Demandes de modification de profil
        </h1>
      </div>
    </div>

    <b-card class="modern-card">
      <div class="card-header">
        <div
          class="stats-row"
          aria-label="Statistiques des demandes de profil"
        >
          <div class="stat-item">
            <div class="stat-icon-wrapper text-primary">
              <feather-icon
                icon="FileTextIcon"
                size="24"
              />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ requests.length }}</span>
            <span class="stat-label">Total</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-warning">
              <feather-icon
                icon="ClockIcon"
                size="24"
              />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ pendingCount }}</span>
            <span class="stat-label">En attente</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-success">
              <feather-icon
                icon="CheckCircleIcon"
                size="24"
              />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ acceptedCount }}</span>
            <span class="stat-label">Acceptées</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-danger">
              <feather-icon
                icon="XCircleIcon"
                size="24"
              />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ rejectedCount }}</span>
            <span class="stat-label">Rejetées</span>
          </div>
        </div>

        <div class="filter-bar mt-4 mb-2 px-0 px-md-3 py-2 d-flex flex-wrap align-items-center justify-content-between">
          <div class="d-flex flex-wrap align-items-center gap-3">
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-filter text-muted mr-2" />
              <select
                v-model="statusFilter"
                class="form-control filter-select"
                aria-label="Filtrer par statut"
                @change="loadRequests"
              >
                <option value="">
                  Tous les statuts
                </option>
                <option value="pending">
                  En attente
                </option>
                <option value="accepted">
                  Acceptée
                </option>
                <option value="rejected">
                  Rejetée
                </option>
              </select>
            </div>
            <button
              class="btn btn-outline-secondary ml-2"
              aria-label="Réinitialiser les filtres"
              @click="statusFilter = ''; loadRequests()"
            >
              <i class="fas fa-times mr-1" /> Réinitialiser
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          small
        />
      </div>

      <div
        v-else-if="!filteredRequests.length"
        class="text-center text-muted py-4"
      >
        Aucune demande trouvée.
      </div>

      <b-table-simple
        v-else
        responsive
        striped
        hover
        class="mb-0"
        aria-label="Liste des demandes de modification de profil"
      >
        <caption class="sr-only">
          Demandes de modification de profil fournisseur
        </caption>
        <b-thead>
          <b-tr>
            <b-th>Date</b-th>
            <b-th>Fournisseur</b-th>
            <b-th>Champ</b-th>
            <b-th>Ancienne valeur</b-th>
            <b-th>Nouvelle valeur</b-th>
            <b-th>Statut</b-th>
            <b-th>Réponse admin</b-th>
            <b-th>Traitée par</b-th>
            <b-th>Traitée le</b-th>
            <b-th>Actions</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="req in filteredRequests"
            :key="req.id"
          >
            <b-td>{{ formatDate(req.created_at) }}</b-td>
            <b-td>
              <div class="font-weight-bold">
                {{ req.user_display || '—' }}
              </div>
            </b-td>
            <b-td>{{ req.field_display || req.field_name }}</b-td>
            <b-td>
              <code class="small">{{ formatValue(req.old_value, req.field_name) }}</code>
            </b-td>
            <b-td>
              <code class="small">{{ formatValue(req.new_value, req.field_name) }}</code>
            </b-td>
            <b-td>
              <b-badge :variant="getStatusVariant(req.status)">
                {{ req.status_display || req.status }}
              </b-badge>
            </b-td>
            <b-td>
              <span
                v-b-tooltip.hover
                :title="req.admin_response || ''"
                class="admin-response-cell"
              >{{ req.admin_response || '—' }}</span>
            </b-td>
            <b-td>{{ req.processed_by_display || '—' }}</b-td>
            <b-td>{{ req.processed_at ? formatDate(req.processed_at) : '—' }}</b-td>
            <b-td>
              <div
                v-if="req.status === 'pending'"
                class="d-flex"
              >
                <b-button
                  variant="outline-success"
                  size="sm"
                  class="mr-1"
                  @click="openActionModal(req, 'accept')"
                >
                  Accepter
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="openActionModal(req, 'reject')"
                >
                  Refuser
                </b-button>
              </div>
              <span
                v-else
                class="text-muted small"
              >
                Traitée
              </span>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </b-card>

    <b-modal
      v-model="actionModal.show"
      :title="actionModal.title"
      centered
      hide-footer
    >
      <p>
        {{ actionModal.question }}
      </p>
      <b-form-group label="Réponse / commentaire (optionnel)">
        <b-form-textarea
          v-model="actionModal.adminResponse"
          rows="3"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="actionModal.show = false"
        >
          Annuler
        </b-button>
        <b-button
          :variant="actionModal.action === 'accept' ? 'success' : 'danger'"
          :disabled="actionModal.isSubmitting"
          @click="submitAction"
        >
          <b-spinner
            v-if="actionModal.isSubmitting"
            small
            class="mr-1"
          />
          Confirmer
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BTableSimple, BThead, BTbody, BTr, BTh, BTd, BBadge, BButton, BSpinner, BModal, BFormGroup, BFormTextarea,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BButton,
    BSpinner,
    BModal,
    BFormGroup,
    BFormTextarea,
  },
  data() {
    return {
      requests: [],
      isLoading: false,
      statusFilter: '',
      actionModal: {
        show: false,
        action: '',
        title: '',
        question: '',
        requestId: null,
        adminResponse: '',
        isSubmitting: false,
      },
    }
  },
  computed: {
    pendingCount() {
      return this.requests.filter(r => r.status === 'pending').length
    },
    acceptedCount() {
      return this.requests.filter(r => r.status === 'accepted').length
    },
    rejectedCount() {
      return this.requests.filter(r => r.status === 'rejected').length
    },
    filteredRequests() {
      if (!this.statusFilter) return this.requests
      return this.requests.filter(r => r.status === this.statusFilter)
    },
  },
  created() {
    this.loadRequests()
  },
  methods: {
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },

    async loadRequests() {
      this.isLoading = true
      try {
        const params = {}
        if (this.statusFilter) params.status = this.statusFilter
        const { data } = await axios.get('/api/auth/profile-change-requests/', { params })
        this.requests = Array.isArray(data) ? data : data.results || []
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des demandes')
      } finally {
        this.isLoading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatValue(value, fieldName) {
      if (value === null || value === undefined || value === '') return '—'
      if (Array.isArray(value)) {
        if (!value.length) return '—'
        const items = value.map(v => {
          if (typeof v === 'object') return JSON.stringify(v)
          if (fieldName === 'invoice_generation_days') return this.getPaymentDayLabel(v)
          return v
        })
        return items.join(', ')
      }
      if (typeof value === 'object') return JSON.stringify(value)
      if (fieldName === 'payment_type') return this.getPaymentTypeLabel(value)
      return String(value)
    },

    getPaymentTypeLabel(type) {
      const labels = {
        espece: 'Espèce',
        virement: 'Virement',
      }
      return labels[type] || type || '—'
    },

    getPaymentDayLabel(day) {
      const labels = {
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
      }
      return labels[day] || day
    },

    getStatusVariant(status) {
      const map = {
        pending: 'warning',
        accepted: 'success',
        rejected: 'danger',
      }
      return map[status] || 'secondary'
    },

    openActionModal(req, action) {
      this.actionModal = {
        show: true,
        action,
        title: action === 'accept' ? 'Accepter la demande' : 'Refuser la demande',
        question: action === 'accept'
          ? `Voulez-vous accepter la demande de modification du champ « ${req.field_display || req.field_name} » ?`
          : `Voulez-vous refuser la demande de modification du champ « ${req.field_display || req.field_name} » ?`,
        requestId: req.id,
        adminResponse: '',
        isSubmitting: false,
      }
    },

    async submitAction() {
      const { action, requestId, adminResponse } = this.actionModal
      this.actionModal.isSubmitting = true
      try {
        await axios.post(`/api/auth/profile-change-requests/${requestId}/${action}/`, {
          admin_response: adminResponse,
        })
        this.showToast(
          'success',
          'top-center',
          action === 'accept' ? 'Demande acceptée avec succès.' : 'Demande refusée avec succès.',
        )
        this.actionModal.show = false
        this.loadRequests()
      } catch (error) {
        // Surface the backend's `detail` (e.g. "Cette demande a déjà été traitée.")
        // and always refresh — otherwise the row keeps stale "Accepter/Refuser"
        // buttons for a request that was processed in another tab.
        const detail = error?.response?.data?.detail
        let message = 'Erreur lors du traitement de la demande'
        if (detail) {
          message = Array.isArray(detail) ? detail.join(' / ') : String(detail)
        }
        this.showToast('danger', 'top-center', message)
        this.actionModal.show = false
        this.loadRequests()
      } finally {
        this.actionModal.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-container {
  padding: 1.5rem;
  animation: slideUp 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.tickets-header {
  background: linear-gradient(135deg, #090909 0%, #9b1c22 100%);
  padding: 2.5rem 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(155, 28, 34, 0.2);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><circle cx="2" cy="2" r="2" fill="rgba(255,255,255,0.05)"/></svg>');
    opacity: 0.5;
  }
}

.simple-title {
  font-weight: 700;
  margin: 0;
  color: white;
  position: relative;
  z-index: 1;
  font-size: 1.8rem;
  letter-spacing: 0.5px;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .stat-item {
    flex: 1;
    min-width: 200px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 1.5rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #9b1c22, #ff4d4d);
      transform: scaleX(0);
      transition: transform 0.3s ease;
      transform-origin: left;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(155, 28, 34, 0.12);

      &::after {
        transform: scaleX(1);
      }
    }

    .stat-icon-wrapper {
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      border-radius: 50%;
      background: rgba(0,0,0,0.03);
    }

    .stat-number {
      font-size: 2.2rem;
      font-weight: 800;
      color: #090909;
      margin-bottom: 0.2rem;
      line-height: 1;
    }
    .stat-label {
      color: #6e6b7b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 0.85rem;
    }
  }
}

.modern-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  background: white;
  overflow: hidden;
}

.filter-bar {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(0,0,0,0.03);
}

.filter-select {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  padding: 0.5rem 1rem;
  transition: all 0.2s;
  &:focus {
    border-color: #9b1c22;
    box-shadow: 0 0 0 0.2rem rgba(155, 28, 34, 0.25);
    outline: none;
  }
}

::v-deep .table-hover tbody tr {
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(155, 28, 34, 0.03) !important;
    transform: scale(1.005);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    border-radius: 8px;
  }
}

.admin-response-cell {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
